import React from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import {
    SchedulesReportLayout,
    SchedulesRollforwardColumn,
} from "@common/types/reports/schedules-report";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import {
    SchedulesReportDrillDownParams,
    SchedulesReportTableMeta,
} from "./schedulesReportDrillDown.lib";
import { SchedulesReportViewRow } from "./schedulesReportViewTypes";
import styles from "./SchedulesReportTable.module.scss";

interface Props {
    value: number | null;
    isPosted: boolean;
    isTotal: boolean;
    isAnalysis: boolean;
    onDrillDown?: () => void;
}

export const SchedulesReportValueCell: React.FC<Props> = React.memo(
    ({ value, isPosted, isTotal, isAnalysis, onDrillDown }) => {
        const { text, negativeClassName } = useReportAmountDisplay(value ?? 0);
        const isInteractive =
            onDrillDown !== undefined && value !== null && value !== 0;
        const displayText = value === null ? "" : text;
        return (
            <ViewTableCell
                className={classNames(styles.numericCell, negativeClassName, {
                    [styles.totalCell]: isTotal,
                    [styles.analysisCell]: isAnalysis,
                    [styles.scheduledCell]: !isPosted && value !== null,
                })}
            >
                {isInteractive ? (
                    <button
                        type="button"
                        className={classNames(
                            "data-table__drill-down",
                            styles.drillDownButton,
                        )}
                        onClick={onDrillDown}
                    >
                        {text}
                    </button>
                ) : (
                    displayText
                )}
            </ViewTableCell>
        );
    },
);

SchedulesReportValueCell.displayName = "SchedulesReportValueCell";

export function makeSchedulesReportSummaryCell(flatIndex: number) {
    const Cell: React.FC<
        CellContext<ViewTableFeatures, SchedulesReportViewRow, unknown>
    > = React.memo(({ row, table }) => {
        const meta = table.options.meta as SchedulesReportTableMeta | undefined;
        const onOpenSchedule = meta?.callbacks?.onOpenSchedule;
        const applicationIds = row.original.drill?.scheduleApplicationIds;
        return (
            <SchedulesReportValueCell
                value={row.original.values[flatIndex] ?? null}
                isPosted
                isTotal={row.original.isTotal}
                isAnalysis={row.original.isAnalysis}
                onDrillDown={
                    onOpenSchedule && applicationIds
                        ? () => onOpenSchedule({ applicationIds })
                        : undefined
                }
            />
        );
    });
    Cell.displayName = `SchedulesReportSummaryCell_${flatIndex}`;
    return Cell;
}

interface ValueCellFactoryParams {
    period: ReportPeriodWithLabel;
    periodIndex: number;
    flatIndex: number;
    layout: SchedulesReportLayout;
    column: SchedulesRollforwardColumn | null;
}

export function makeSchedulesReportValueCell({
    period,
    periodIndex,
    flatIndex,
    layout,
    column,
}: ValueCellFactoryParams) {
    const Cell: React.FC<
        CellContext<ViewTableFeatures, SchedulesReportViewRow, unknown>
    > = React.memo(({ row, table }) => {
        const meta = table.options.meta as SchedulesReportTableMeta | undefined;
        const onDrillDown = meta?.callbacks?.onDrillDown;
        const { drill } = row.original;
        const isPosted = row.original.isPostedByPeriod?.[periodIndex] ?? true;
        const canDrill =
            onDrillDown !== undefined &&
            drill !== undefined &&
            isPosted &&
            !row.original.isAnalysis;
        const params: SchedulesReportDrillDownParams | null = drill
            ? { drill, period, layout, column }
            : null;
        return (
            <SchedulesReportValueCell
                value={row.original.values[flatIndex] ?? null}
                isPosted={isPosted}
                isTotal={row.original.isTotal}
                isAnalysis={row.original.isAnalysis}
                onDrillDown={
                    canDrill && params ? () => onDrillDown(params) : undefined
                }
            />
        );
    });
    Cell.displayName = `SchedulesReportValueCell_${flatIndex}`;
    return Cell;
}
